import { useState, type ReactNode } from 'react';
import { Skeleton } from './Skeleton';
import { ImageLightbox } from './ImageLightbox';

/**
 * An <img> that shimmers while it loads. Shared by the chat bubbles and the
 * task comments — both show images that arrive over a signed proxy link.
 *
 * On a slow connection the spot would otherwise be an empty gap with no hint
 * that a picture is coming. Until `onLoad` the image is forced to a placeholder
 * box and kept invisible: that gives the wrapper a real size for the
 * absolutely-positioned skeleton to cover, and on load the image drops back to
 * its natural dimensions and fades in.
 *
 * A failed load (expired signed URL, deleted file) renders `fallback` instead
 * of a broken-image icon.
 *
 * Callers key this by the attachment's name and position, never by its URL:
 * the download URL carries a signed token that the server re-mints, so a URL
 * key remounts the image on every refetch and the picture restarts from the
 * skeleton (BUG-8). A changed `src` on a mounted <img> keeps the old bitmap
 * on screen until the new one has arrived.
 *
 * The wrapper is a <span>, not a <div>: inline markdown images render inside
 * <p>/<li>, where only phrasing content is valid. The zoom trigger is a
 * <button> for the same reason — also phrasing content, and keyboard-reachable,
 * which the old <a target="_blank"> wrappers at the call sites were only by
 * accident.
 */
export function AttachmentImage({
  src,
  alt,
  className,
  placeholderClassName,
  fallback = null,
  zoomable = true,
}: {
  src: string;
  alt: string;
  /** Classes for the loaded image. */
  className: string;
  /** Size the image is pinned to while loading (also the skeleton's size). */
  placeholderClassName: string;
  fallback?: ReactNode;
  /**
   * Click opens the full-screen preview. On by default: everywhere a picture
   * is shown, looking at it closely should not mean leaving the page. Turn it
   * off for a thumbnail that decorates a link (the embed card), where the
   * click belongs to the link.
   */
  zoomable?: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  if (failed) return <>{fallback}</>;

  const image = (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
      className={`${className} transition-opacity duration-200 ${
        loaded ? 'opacity-100' : `${placeholderClassName} opacity-0`
      }`}
    />
  );

  return (
    <span className="relative block">
      {!loaded && <Skeleton className="absolute inset-0 h-full w-full rounded-md" />}
      {zoomable ? (
        <button
          type="button"
          onClick={() => setZoomed(true)}
          aria-label={alt ? `Mărește imaginea ${alt}` : 'Mărește imaginea'}
          className="block cursor-zoom-in appearance-none border-0 bg-transparent p-0"
        >
          {image}
        </button>
      ) : (
        image
      )}
      {zoomed && <ImageLightbox src={src} alt={alt} onClose={() => setZoomed(false)} />}
    </span>
  );
}
